import * as React from 'react';

// Externals
import * as _ from 'lodash';
import { t } from '@lingui/macro';
import { i18n } from '@lingui/core';

// Components
import CitySelector from '../citySelector';
import LocationModel from '../../models/locationModel';

// Models

class ILocationInputProps {
  defaultCountry?: string;
  hideCountry?: boolean;
  locationId?: number;
  onChangeLocationId?: (number) => void;
  onChangeLocation?: (LocationModel) => void;
  onChangeProvinceLocationId?: (number) => void;
  provinceFilter?: boolean;
  excludeLocations?: Array<number> = [];
  showLocationIdError?: boolean;
  required?: boolean;
  id?: string;
}

class ILocationInputState {
  countryCode: string;
  locations: Array<LocationModel> = [];
  loading: boolean = true;
  selectedProvince: LocationModel;
}

const STORAGE_KEY = { fr: 'syndy-locations-fr-v3', nl: 'syndy-locations-nl-v3' };
const LEGACY_STORAGE_KEY = ['syndy-locations-v2', 'syndy-locations'];


export default class LocationInput extends React.Component<
  ILocationInputProps,
  ILocationInputState
> {
  constructor(props) {
    super(props);

    this.state = new ILocationInputState();
  }

  componentDidMount = () => {
    try {
      for (const key of LEGACY_STORAGE_KEY) {
        localStorage.removeItem(key);
      }

      if (i18n.locale === 'fr') {
        localStorage.removeItem(STORAGE_KEY.nl);
      } else {
        localStorage.removeItem(STORAGE_KEY.fr);
      }

      var text = localStorage.getItem(STORAGE_KEY[i18n.locale]);

      if (text) {
        var locations = JSON.parse(text) || [];

        if (locations.length) {
          this.setState({ locations });

          if (this.props.locationId) {
            const location = locations && locations.find((l) => l.id == this.props.locationId);
            if (location) this.setState({ countryCode: location.countryCode });
          }

          this.setState({ loading: false });

          return;
        }
      }
    } catch {
    }

    this.fetchLocations();
  };

  fetchLocations = async () => {
    this.setState({ loading: true });

    let response = await fetch(`/api/location`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      credentials: 'include'
    });

    if (response.ok) {
      var text = await response.text();

      try {
        localStorage.setItem(STORAGE_KEY[i18n.locale], text);
      } catch (e) {
        console.error(e);
      }

      let locations = JSON.parse(text);

      // Save locations
      this.setState({ locations: locations ?? [] });

      if (this.props.locationId) {
        const location = locations && locations.find((l) => l.id == this.props.locationId);
        if (location) this.setState({ countryCode: location.countryCode });
      }
    } else {
      let error = await response.text();
      console.log(error);
    }

    this.setState({ loading: false });
  };

  handleProvinceChange = (provinceId: string) => {
    const selectedProvince = this.state.locations.find((l) => l.id == parseInt(provinceId));
    this.setState({ selectedProvince });
    this.props.onChangeProvinceLocationId(selectedProvince ? selectedProvince.id : null);
  };

  getProvinceFilterLabel = (countryCode: string) => {
    switch (countryCode) {
      case 'BE':
        return t`Toutes les provinces`;
      case 'FR':
        return t`Tous les départements`;
      case 'LU':
        return t`Tous les cantons`;
      default:
        return t`Sélectionnez d'abord un pays`;
    }
  }

  render() {
    let { locations, loading } = this.state;

    let { hideCountry, locationId, onChangeLocationId, onChangeLocation, showLocationIdError } =
      this.props;

    const location = locations && locations.find((l) => l.id == locationId);

    const currentCountryCode =
      this.props.defaultCountry ||
      (location && location.countryCode) ||
      this.state.countryCode ||
      '';

    const provinces = _.sortBy(locations.filter((l) => l.level === 2 && l.countryCode === currentCountryCode), ['name']);

    const filteredBySelectedProvinceLocations = this.state.selectedProvince && this.props.provinceFilter ? _.sortBy(
      locations.filter((l) => l.countryCode === currentCountryCode && l.provinceLocationId === this.state.selectedProvince.id),
      ['code']
    ) : _.sortBy(locations.filter((l) => l.countryCode === currentCountryCode && l.level === 4), ['code']);

    if (hideCountry) {
      return (
        <CitySelector
          disabled={loading}
          loading={loading}
          id={this.props.id}
          required={this.props.required}
          selectedValue={location}
          className={'field w-select' + (showLocationIdError ? ' error' : '')}
          excludeLocations={this.props.excludeLocations || []}
          zipCodes={filteredBySelectedProvinceLocations}
          onSelect={(location) => {
            onChangeLocationId && onChangeLocationId((location && location.id) || null);
            onChangeLocation && onChangeLocation(location);
          }}
        />
      );
    }

    return (
      <div style={{ width: '100%', display: 'flex' }}>
        <div className="row" style={{ flex: 1 }}>
          <div className="col-12 col-md-3">
            <select
              className="field w-select"
              name="Country"
              data-name="Country"
              id="Country"
              disabled={loading}
              value={currentCountryCode}
              onChange={(event) => {
                if (event.target.value !== currentCountryCode) {
                  this.setState({ countryCode: event.target.value });
                  onChangeLocationId && onChangeLocationId(null);
                }
              }}>
              <option value="" disabled>
                {t`Pays`}
              </option>
              <option value="BE">{t`Belgique`}</option>
              <option value="FR">{t`France`}</option>
              <option value="LU">{t`Luxembourg`}</option>
            </select>
          </div>

          {this.props.provinceFilter && (
            <div className="col-12 col-md-6 col-lg-3">
              <select
                id="province"
                name="province"
                data-name="province"
                className="field w-select"
                disabled={loading || !currentCountryCode}
                onChange={(event) => {
                  this.handleProvinceChange(event.target.value);
                }}
              >
                <option value="">{this.getProvinceFilterLabel(currentCountryCode)}</option>
                {provinces.map((province) => (
                  <option key={province.id} value={province.id}>
                    {province.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="col-12 col-md-3">
            <CitySelector
              disabled={loading || !currentCountryCode}
              loading={loading}
              id={this.props.id}
              required={this.props.required}
              selectedValue={location}
              className={'field w-select' + (showLocationIdError ? ' error' : '')}
              excludeLocations={this.props.excludeLocations || []}
              zipCodes={filteredBySelectedProvinceLocations}
              onSelect={(location) => {
                onChangeLocationId && onChangeLocationId((location && location.id) || null);
                onChangeLocation && onChangeLocation(location);
              }}
            />
          </div>
        </div>
      </div>
    );
  }
}
